import { Button, CircularProgress, TextField } from "@mui/material";
import { API_URL } from "const/constants";
import useKothar from "context/useKothar";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteData } from "const/axios";
import { toast } from "react-toastify";
import { IoArrowBack } from "react-icons/io5";
import { formatDisplayDate } from "utils/dateFormat";
const normalize = (value) => (value || "").trim().toLowerCase().replace(/\s+/g, " ");

const DuplicateClients = () => {
  const [{ clientList = [], isClientLoading, token }, { refetchClient }] = useKothar();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (!clientList?.length) {
      refetchClient();
    }
  }, []);

  const duplicateGroups = useMemo(() => {
    if (!Array.isArray(clientList)) return [];

    const grouped = clientList.reduce((acc, client) => {
      const normalizedName = normalize(client?.name);
      const normalizedPassport = normalize(client?.passportNumber);

      // Only consider records that have BOTH name and passport number.
      if (!normalizedName || !normalizedPassport) return acc;

      const key = `${normalizedName}__${normalizedPassport}`;
      if (!acc[key]) {
        acc[key] = {
          key,
          normalizedName,
          normalizedPassport,
          name: client?.name || "-",
          passportNumber: client?.passportNumber || "-",
          records: [],
        };
      }

      acc[key].records.push(client);
      return acc;
    }, {});

    return Object.values(grouped)
      .filter((group) => group.records.length > 1)
      .sort((a, b) => b.records.length - a.records.length);
  }, [clientList]);

  const filteredGroups = useMemo(() => {
    const q = normalize(search);
    if (!q) return duplicateGroups;

    return duplicateGroups.filter(
      (group) =>
        normalize(group.name).includes(q) ||
        normalize(group.passportNumber).includes(q) ||
        group.records.some(
          (record) =>
            normalize(record?.email).includes(q) ||
            normalize(record?.number).includes(q) ||
            normalize(record?.caseOfficer).includes(q)
        )
    );
  }, [duplicateGroups, search]);

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      deleteData(
        `${API_URL}/clients/${id}`,
        () => {
          toast.success("Client deleted successfully");
          refetchClient();
        },
        token
      );
    }
  };

  if (isClientLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-app-surface text-app-text">
          <div className="px-4 py-4 border-b border-app-border flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-app-text flex items-center gap-2">
                <IoArrowBack className="text-xl cursor-pointer mr-2" onClick={() => navigate(-1)} />
                Duplicate Clients
              </h2>
              <p className="text-sm text-app-text-muted mt-1">
                Showing duplicates based on matching <b>Name + Passport Number</b>.
              </p>
            </div>
            <TextField
              size="small"
              placeholder="Search name, passport, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: { xs: "100%", md: 340 } }}
            />
          </div>

          <div className="p-4">
            {filteredGroups.length === 0 ? (
              <div className="text-center text-app-text-muted py-12">
                No duplicate clients found for Name + Passport Number.
              </div>
            ) : (
              <div className="space-y-4">
                {filteredGroups.map((group) => (
                  <div
                    key={group.key}
                    className="border border-app-border rounded-lg overflow-hidden"
                  >
                    <div className="bg-app-surface-muted px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="text-sm text-app-text">
                        <span className="font-semibold">{group.name}</span>
                        <span className="mx-2 text-app-text-muted">|</span>
                        Passport: <span className="font-medium">{group.passportNumber}</span>
                      </div>
                      <span className="inline-flex items-center w-fit px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                        {group.records.length} duplicates
                      </span>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-app-surface border-b border-app-border">
                          <tr>
                            <th className="text-left px-4 py-2 font-semibold text-app-text-secondary">
                              ID
                            </th>
                            <th className="text-left px-4 py-2 font-semibold text-app-text-secondary">
                              Email
                            </th>
                            <th className="text-left px-4 py-2 font-semibold text-app-text-secondary">
                              Phone
                            </th>
                            <th className="text-left px-4 py-2 font-semibold text-app-text-secondary">
                              Case Officer
                            </th>
                            <th className="text-left px-4 py-2 font-semibold text-app-text-secondary">
                              Created
                            </th>
                            <th className="text-left px-4 py-2 font-semibold text-app-text-secondary">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.records.map((record) => (
                            <tr
                              key={record.id}
                              className="border-b border-app-border last:border-b-0"
                            >
                              <td className="px-4 py-2 text-app-text">{record.id}</td>
                              <td className="px-4 py-2 text-app-text">{record.email || "-"}</td>
                              <td className="px-4 py-2 text-app-text">{record.number || "-"}</td>
                              <td className="px-4 py-2 text-app-text">
                                {record.caseOfficer || "-"}
                              </td>
                              <td className="px-4 py-2 text-app-text">
                                {formatDisplayDate(record.createdAt)}
                              </td>
                              <td className="px-4 py-2 text-app-text">
                                <div className="flex items-center gap-2">
                                  <span
                                    className="text-blue-500 cursor-pointer"
                                    onClick={() => navigate(`/admin/client/view/${record.id}`)}
                                  >
                                    View
                                  </span>
                                  <span
                                    className="text-red-500 cursor-pointer"
                                    onClick={() => handleDelete(record.id)}
                                  >
                                    Delete
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateClients;
